import { Code } from '@launchpad-ui/components';
import clsx from 'clsx';

import type { ContextKind } from '../../types/types';

import styles from './ContextKindLabel.module.css';

type ContextKindLabelProps = {
  /** The context kind to display. Only `name` and `key` are read. */
  contextKind: Pick<ContextKind, 'name' | 'key'>;
  /**
   * - `full` (default): the human-readable name with the key shown as a code badge in support.
   *   Falls back to the key alone when the kind has no name.
   * - `key-only`: just the key badge. Use when space is constrained or the surface is highly technical.
   */
  variant?: 'full' | 'key-only';
  /**
   * - `stacked` (default): name on the first line, key code badge on the second. Matches the
   *   context kind selection popover (Figma node 2:12692).
   * - `inline`: name and key on a single line, for tight or in-line surfaces.
   */
  layout?: 'stacked' | 'inline';
  className?: string;
};

/**
 * Canonical display for a context kind: the human-readable name (LaunchPad `label/1/medium`)
 * leads and the unique key follows in LaunchPad `Code` styling. Never renders the name alone —
 * names aren't guaranteed unique and can encode messages like "DO NOT USE", so the key always
 * disambiguates.
 *
 * Display rules follow the Context Kind Display Guidelines (Figma node 3:12893).
 */
export const ContextKindLabel = ({
  contextKind,
  variant = 'full',
  layout = 'stacked',
  className,
}: ContextKindLabelProps) => {
  // Key alone — explicit request, or no name to lead with.
  if (variant === 'key-only' || !contextKind.name) {
    return (
      <Code size="small" className={className}>
        {contextKind.key}
      </Code>
    );
  }

  return (
    <span className={clsx(styles.contextKindLabel, styles[layout], className)}>
      <span className={styles.name}>{contextKind.name}</span>
      <Code size="small" className={styles.key}>
        {contextKind.key}
      </Code>
    </span>
  );
};
